/**
 * Códigos de barras para las etiquetas de artículos: EAN-13 y Code128.
 *
 * Devuelven módulos ("1" = barra) y la etiqueta los dibuja a la escala que
 * necesite, igual en la vista previa (SVG) que en el PDF.
 */

export interface Barcode {
  symbology: 'ean13' | 'code128'
  /** Lo que se escanea y se imprime debajo de las barras. */
  text: string
  modules: string
  /** Posiciones de las barras guía del EAN-13, que van más largas. */
  guards: Set<number>
  quietLeft: number
  quietRight: number
}

const EAN_L = ['0001101', '0011001', '0010011', '0111101', '0100011', '0110001', '0101111', '0111011', '0110111', '0001011']
const EAN_G = ['0100111', '0110011', '0011011', '0100001', '0011101', '0111001', '0000101', '0010001', '0001001', '0010111']
const EAN_R = ['1110010', '1100110', '1101100', '1000010', '1011100', '1001110', '1010000', '1000100', '1001000', '1110100']
const EAN_PARITY = ['LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG', 'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL']
const EAN_GUARDS = new Set([0, 1, 2, 45, 46, 47, 48, 49, 92, 93, 94])

export function ean13CheckDigit(first12: string): number {
  const total = [...first12].reduce((sum, d, i) => sum + Number(d) * (i % 2 ? 3 : 1), 0)
  return (10 - (total % 10)) % 10
}

/**
 * EAN-13 de uso interno para un código interno numérico: "2" + el código a 11
 * dígitos + control. El prefijo 2 es de circulación restringida (GS1), así que
 * no choca con un código de fábrica. El backend lo traduce de vuelta al código
 * interno (`label_internal_code`).
 *
 * Los códigos con ceros a la izquierda no entran: "00123" volvería como "123".
 */
export function internalEan13(code: string): string | null {
  if (!/^[1-9]\d{0,10}$/.test(code)) return null
  const base = '2' + code.padStart(11, '0')
  return base + ean13CheckDigit(base)
}

export function ean13(number: string): Barcode {
  const first = Number(number[0])
  let modules = '101'
  for (let i = 0; i < 6; i++) {
    const digit = Number(number[i + 1])
    modules += (EAN_PARITY[first][i] === 'L' ? EAN_L : EAN_G)[digit]
  }
  modules += '01010'
  for (let i = 7; i < 13; i++) modules += EAN_R[Number(number[i])]
  modules += '101'
  return { symbology: 'ean13', text: number, modules, guards: EAN_GUARDS, quietLeft: 11, quietRight: 7 }
}

// Anchos barra/espacio de los valores 0 a 106 (106 = stop).
const CODE128 = (
  '212222 222122 222221 121223 121322 131222 122213 122312 132212 221213 ' +
  '221312 231212 112232 122132 122231 113222 123122 123221 223211 221132 ' +
  '221231 213212 223112 312131 311222 321122 321221 312212 322112 322211 ' +
  '212123 212321 232121 111323 131123 131321 112313 132113 132311 211313 ' +
  '231113 231311 112133 112331 132131 113123 113321 133121 313121 211331 ' +
  '231131 213113 213311 213131 311123 311321 331121 312113 312311 332111 ' +
  '314111 221411 431111 111224 111422 121124 121421 141122 141221 112214 ' +
  '112412 122114 122411 142112 142211 241211 221114 413111 241112 134111 ' +
  '111242 121142 121241 114212 124112 124211 411212 421112 421211 212141 ' +
  '214121 412121 111143 111341 131141 114113 114311 411113 411311 113141 ' +
  '114131 311141 411131 211412 211214 211232 2331112'
).split(' ')
const START_B = 104
const START_C = 105
const CODE_B = 100
const CODE_C = 99

/** Code128 con el conjunto B, y C en los tramos de dígitos, que lo acortan. */
export function code128(text: string): Barcode {
  if (!/^[\x20-\x7e]+$/.test(text)) throw new Error('Code128: solo ASCII imprimible')
  const values: number[] = []
  let set: 'B' | 'C' | null = null
  let i = 0
  while (i < text.length) {
    const digits = /^\d*/.exec(text.slice(i))![0].length
    // Pasar a C solo compensa con 4 dígitos o más (o el código entero en pares).
    const useC = digits >= 4 || (digits === text.length && digits % 2 === 0)
    if (useC) {
      const pairs = Math.floor(digits / 2)
      if (set !== 'C') values.push(set === null ? START_C : CODE_C)
      set = 'C'
      for (let p = 0; p < pairs; p++, i += 2) values.push(Number(text.slice(i, i + 2)))
      continue
    }
    if (set !== 'B') values.push(set === null ? START_B : CODE_B)
    set = 'B'
    values.push(text.charCodeAt(i) - 32)
    i++
  }
  const checksum = values.reduce((sum, v, idx) => sum + v * (idx === 0 ? 1 : idx), 0) % 103
  values.push(checksum, 106)

  let modules = ''
  for (const v of values) {
    ;[...CODE128[v]].forEach((w, k) => {
      modules += (k % 2 ? '0' : '1').repeat(Number(w))
    })
  }
  return { symbology: 'code128', text, modules, guards: new Set(), quietLeft: 10, quietRight: 10 }
}

/**
 * El código de la etiqueta de un artículo: siempre su código interno, que es
 * único. Null si lleva caracteres que Code128 no tiene (tildes, ñ).
 */
export function articleBarcode(code: string): Barcode | null {
  const value = code.trim()
  const ean = internalEan13(value)
  if (ean) return ean13(ean)
  return /^[ -~]+$/.test(value) ? code128(value) : null
}
